/**
 * "Refly list" on a project in Teslyn Cloud (R24, decision 503; help `cloud.refly-list`). Staff see the master refly
 * list as it is now, computed from what Teslyn Field synced (`@teslyn/domain` reflyRows: every rejected and part-flown
 * line with its reflight or patch, its ends and ZFIDs, reason and status), with the km still to refly; any staff
 * member publishes it to the client, and stops showing it (decision 543). The client (and Preview as client) sees only the list
 * published last, read only. Everyone downloads what they see as the F26019 workbook.
 */
import { Fragment, useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  bearingWords,
  canPublishReflyList,
  canTickRefly,
  dateToJday,
  isOpenRefly,
  localCalendarDate,
  reflyClientRows,
  reflyRows,
  reflyTotals,
  reflyWorkbook,
  reflyWorkbookName,
  REFLY_STATUS_WORDS,
  REFLY_TICK_WORDS,
  type ReflyInput,
  type ReflyRow,
  type ReflyTickKind,
  type ReflyTotals,
  formatZfid,
} from '@teslyn/domain';
import { writeXlsx, XLSX_TYPE } from '@teslyn/sync';
import { Button } from '../components/Button.tsx';
import { TickCell } from '../components/TickCell.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import type { PortalProject } from './api.ts';
import {
  addReflyTick,
  loadPublishedRefly,
  loadReflyInput,
  publishRefly,
  withdrawRefly,
  type ReflyPublication,
} from './reflyApi.ts';
import { TableWrap } from '../components/TableWrap.tsx';

/** Offer bytes to the browser as a download. */
function browserDownload(fileName: string, bytes: Uint8Array): void {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: XLSX_TYPE }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const km = (v: number): string =>
  v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const m2 = (v: number): string =>
  v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATE: Readonly<Record<ReflyRow['status'], string>> = {
  waiting: 'qc qc--fail label',
  in_plan: 'qc qc--warn label',
  flown: 'qc qc--pending label',
  accepted: 'qc qc--pass label',
};

/** A file name that may break after each underscore or full stop, so a long name takes two lines, never the row. */
function breakable(name: string): ReactNode {
  return name.split(/(?<=[_.])/).map((part, i) => (
    <Fragment key={i}>
      {i > 0 && <wbr />}
      {part}
    </Fragment>
  ));
}

export interface ReflyTabProps {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly mode: 'staff' | 'client';
  readonly roles?: readonly string[];
  /** Tests pass their own. */
  readonly download?: (fileName: string, bytes: Uint8Array) => void;
}

export function ReflyTab({
  sb,
  project,
  mode,
  roles = [],
  download = browserDownload,
}: ReflyTabProps): ReactElement {
  const staff = mode === 'staff';
  const mayPublish = staff && project.status !== 'archived' && canPublishReflyList(roles);
  const [rows, setRows] = useState<ReflyRow[] | null>(null);
  const [totals, setTotals] = useState<ReflyTotals | null>(null);
  const [pub, setPub] = useState<ReflyPublication | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [round, setRound] = useState(0);
  const reload = useCallback(() => setRound((r) => r + 1), []);
  // staff: what the rows are made from, so a tick shows at once
  const [input, setInput] = useState<ReflyInput | null>(null);
  const mayTick = staff && project.status !== 'archived' && canTickRefly(roles);

  useEffect(() => {
    let live = true;
    setError(null);
    void (async () => {
      try {
        if (staff) {
          const [input, published] = await Promise.all([
            loadReflyInput(sb, project.id),
            loadPublishedRefly(sb, project.id),
          ]);
          const r = reflyRows(input);
          if (!live) return;
          setInput(input);
          setRows(r);
          setTotals(reflyTotals(r));
          setPub(published);
        } else {
          const published = await loadPublishedRefly(sb, project.id);
          if (!live) return;
          setPub(published);
          setRows(published?.rows ?? []);
          setTotals(published?.totals ?? null);
        }
      } catch (e: unknown) {
        if (live) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [sb, project.id, staff, round]);

  const title = `${project.number} ${project.clientOrgName ?? ''} ${project.area}`
    .replace(/\s+/g, ' ')
    .trim();

  async function publish(): Promise<void> {
    if (!rows || !totals) return;
    setBusy(true);
    setNote(null);
    try {
      const p = await publishRefly(sb, {
        tenantId: project.tenantId,
        projectId: project.id,
        title,
        // the client never sees who ticked, the lines files or the reasons as typed
        rows: reflyClientRows(rows),
        totals,
        asOf: new Date().toISOString(),
      });
      setPub(p);
      setNote(`Published: the client sees these ${rows.length} rows, as of now, and can download them.`);
    } catch (e: unknown) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /** Decision 544: tick or untick a row; a new row each time, the database stamps who. */
  async function setTick(r: ReflyRow, kind: ReflyTickKind, ticked: boolean): Promise<void> {
    if (!input) return;
    setNote(null);
    try {
      const t = await addReflyTick(sb, {
        tenantId: project.tenantId,
        projectId: project.id,
        rowKey: r.id,
        lineStatusId: r.line_status_id,
        tick: kind,
        ticked,
      });
      const next = { ...input, ticks: [...(input.ticks ?? []), t] };
      setInput(next);
      setRows(reflyRows(next));
    } catch (e: unknown) {
      setNote(`Not ticked. ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function withdraw(): Promise<void> {
    if (!pub) return;
    setBusy(true);
    try {
      await withdrawRefly(sb, pub.id);
      setPub(null);
      setNote('The client no longer sees the refly list.');
    } catch (e: unknown) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function workbook(): void {
    if (!rows || !totals) return;
    const at = staff ? new Date() : new Date(pub?.as_of ?? Date.now());
    const asOf = at.toLocaleString('en-AU');
    const bytes = writeXlsx(
      reflyWorkbook(shown, staff ? reflyTotals(shown) : totals, { title, asOf }),
      at.toISOString(),
    );
    download(reflyWorkbookName(project.number, dateToJday(localCalendarDate(at))), bytes);
  }

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!rows) return <Loading what="the refly list" />;
  if (!staff && !pub)
    return (
      <div className="panel">
        <p className="body">No refly list has been published for this project yet.</p>
      </div>
    );
  const shown = showAll || !staff ? rows : rows.filter(isOpenRefly);
  const t = totals;

  return (
    <div className="stack">
      <div className="panel stack">
        {t && (
          <p className="body">
            <strong className="data">{t.open_rows}</strong> open row{t.open_rows === 1 ? '' : 's'},{' '}
            <strong className="data">{km(t.open_km)} km</strong> to refly
            {t.by_reason.length > 0
              ? `: ${t.by_reason.map((r) => `${r.reason} ${km(r.km)} km`).join(', ')}`
              : ''}
            .
          </p>
        )}
        {!staff && pub && (
          <p className="caption muted">
            As of {new Date(pub.as_of).toLocaleString('en-AU')}, published by your survey company.
          </p>
        )}
        {staff && (
          <p className="caption">
            {pub
              ? `The client sees the list published ${new Date(pub.published_at ?? pub.as_of).toLocaleString('en-AU')}${pub.source === 'field' ? ' from Teslyn Field' : ''}.`
              : 'The client does not see a refly list.'}
            {!mayPublish &&
              project.status === 'archived' &&
              ' An archived project’s list cannot be published.'}
          </p>
        )}
        {staff && !project.clientOrgId && (
          <p className="caption" role="note">
            No client organisation is linked to this project, so no client sees the list, published or not.
            Choose one under &ldquo;Client organisation&rdquo; at the top of the project page.
          </p>
        )}
        <div className="row row--wrap">
          {staff && (
            <Button variant="quiet" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show open rows' : 'Show all rows'}
            </Button>
          )}
          <Button variant="secondary" disabled={shown.length === 0} onClick={workbook}>
            Download Excel
          </Button>
          {mayPublish && (
            <Button busy={busy} disabled={rows.length === 0} onClick={() => void publish()}>
              Publish to client portal
            </Button>
          )}
          {mayPublish && pub && (
            <Button variant="quiet" busy={busy} onClick={() => void withdraw()}>
              Stop showing the client
            </Button>
          )}
        </div>
        {note && (
          <p className="body" role="status">
            {note}
          </p>
        )}
      </div>
      {shown.length === 0 ? (
        <div className="panel">
          <p className="body">{rows.length === 0 ? 'Nothing to refly.' : 'Every reflight is accepted.'}</p>
        </div>
      ) : (
        <TableWrap>
          {/* MB, 25 Oct 2026: wide enough for every column. Each end's time over its ZFID and east over north, under
              Start and End, and the lines file under the status, so a row fits beside the menu 1366 px wide */}
          <table className="table table--refly" aria-label="Refly list">
            <thead>
              <tr>
                <th className="label num" rowSpan={2}>
                  Flt#
                </th>
                <th className="label" rowSpan={2}>
                  Line&nbsp;#
                </th>
                <th className="label" rowSpan={2}>
                  Reason
                </th>
                <th className="label table__group" colSpan={2} scope="colgroup">
                  Start
                </th>
                <th className="label table__group" colSpan={2} scope="colgroup">
                  End
                </th>
                <th className="label num" rowSpan={2}>
                  Length (m)
                </th>
                <th className="label" rowSpan={2}>
                  Block
                </th>
                <th className="label" rowSpan={2}>
                  Direction
                </th>
                <th className="label" rowSpan={2}>
                  Checked
                </th>
                <th className="label" rowSpan={2}>
                  Added to aircraft
                </th>
                <th className="label" rowSpan={2}>
                  {staff ? 'Status and lines file' : 'Status'}
                </th>
              </tr>
              <tr>
                {(['start', 'end'] as const).map((end) => (
                  <Fragment key={end}>
                    <th className="label num" aria-label={`Time and ZFID ${end}`}>
                      <span className="table__pair">
                        <span>Time</span>
                        <span>ZFID</span>
                      </span>
                    </th>
                    <th className="label num" aria-label={`East and north ${end}`}>
                      <span className="table__pair">
                        <span>East</span>
                        <span>North</span>
                      </span>
                    </th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id}>
                  <td className="num data">{r.flt_num ?? ''}</td>
                  <td className="data">
                    {r.line_label}
                    {r.section != null && (
                      <span className="caption table__second">
                        section {r.section} of {r.section_count}
                      </span>
                    )}
                  </td>
                  <td className="table__text" title={r.reason_text}>
                    {r.reason}
                  </td>
                  {([r.start, r.end] as const).map((e, i) => (
                    <Fragment key={i}>
                      <td className="num data">
                        <span className="table__pair">
                          <span>{e.time ?? ''}</span>
                          <span>{formatZfid(e.zfid, '')}</span>
                        </span>
                      </td>
                      <td className="num data">
                        <span className="table__pair">
                          <span>{m2(e.x)}</span>
                          <span>{m2(e.y)}</span>
                        </span>
                      </td>
                    </Fragment>
                  ))}
                  <td className="num data">{m2(r.length_m)}</td>
                  <td className="table__text">{r.block_name}</td>
                  <td className="data">{bearingWords(r.direction_deg)}</td>
                  {(['checked', 'on_aircraft'] as const).map((kind) => {
                    const t = kind === 'checked' ? r.checked : r.on_aircraft;
                    const line =
                      r.section != null
                        ? `line ${r.line_label} section ${r.section}`
                        : `line ${r.line_label}`;
                    return (
                      <td key={kind}>
                        {staff ? (
                          <>
                            <TickCell
                              aria-label={`${REFLY_TICK_WORDS[kind]}: ${line}`}
                              checked={t?.ticked ?? false}
                              disabled={!mayTick}
                              onChange={(e) => void setTick(r, kind, e.target.checked)}
                            />
                            {t && (
                              <span
                                className="caption muted table__second"
                                title={`${t.ticked ? 'Ticked' : 'Unticked'} by ${t.by_name}, ${new Date(t.at).toLocaleString('en-AU')}`}
                              >
                                {t.ticked ? '' : 'unticked, '}
                                {t.by_name}
                              </span>
                            )}
                          </>
                        ) : t?.ticked ? (
                          // decision 565: the client sees who ticked
                          <>
                            Yes
                            {t.by_name && <span className="caption muted table__second">{t.by_name}</span>}
                          </>
                        ) : (
                          ''
                        )}
                      </td>
                    );
                  })}
                  <td>
                    <span className={STATE[r.status]}>{REFLY_STATUS_WORDS[r.status]}</span>
                    {staff && r.lines_file && (
                      <span className="caption table__second">{breakable(r.lines_file)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}
